import styles from './DeliveryPayModal.module.css';

import type { FC } from 'react';
import type { DealChain } from 'src/utils/dealChain';
import type { ApiDeliveryDealDetail } from 'src/types/delivery';

import React, { useState } from 'react';
import { Truck, Warning, CheckCircle } from '@phosphor-icons/react';

import { useEscrowStash } from 'src/hooks/useEscrowStash';

import { withWalletTimeout } from 'src/utils/walletTimeout';
import { readDeliveryContractInfo } from 'src/utils/deliveryOnchain';
import { rememberFlow, flowInFlight, FLOW_IN_FLIGHT_MESSAGE } from 'src/utils/escrowFlows';
import {
  deliveryGasSun,
  hasGasCompensation,
  deliveryInvoiceView,
  deliveryInvoiceLines,
} from 'src/utils/deliveryInvoice';
import {
  payEscrow,
  deliveryCreateCall,
  ESCROW_ETH_BALANCE_HINT,
  ESCROW_NETWORK_FEES_NOTE,
} from 'src/utils/escrowCreation';

import { useWalletUI, WalletButton } from 'src/blockchain';
import { useDeliveryGasPaid, useFetchDeliveryDeal, useDeliveryEscrowCreated } from 'src/queries';

import { Modal } from 'src/components/Modal';
import { Stack } from 'src/components/themed/Stack';
import { InvoiceCard } from 'src/components/InvoiceCard';
import { Typography } from 'src/components/themed/Typography';
import { TxProgress, useTxProgress, useClearWhenWalletConnects } from 'src/components/TxProgress';

import { DeliveryGasStatus, DeliveryDealStatus } from 'src/types/delivery';

export type DeliveryPayMode = 'fund' | 'gas';

// A sent-but-not-yet-reported gas tx hash is stashed here so a failed report never leads to paying
// the gas twice: the next attempt re-reports the stashed hash instead of sending a fresh transfer.
// Best-effort — private-mode localStorage throws are swallowed (protection degrades to the pre-fix
// behaviour, never worse). Legacy TRON deals only; v3 deals on Base have no gas line at all.
const gasTxStashKey = (dealId: string) => `delivery-gas-tx:${dealId}`;
const readStash = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeStash = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
};
const clearStash = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
};

interface DeliveryPayModalProps {
  visible: boolean;
  onClose: () => void;
  deal: ApiDeliveryDealDetail;
  /** The deal's network — decides the wallet family, the token approved and the chain switched to. */
  chain: DealChain | null;
  /** 'fund' = gas (if owed) + approve + fund; 'gas' = the standalone legacy gas tail only. */
  mode: DeliveryPayMode;
  /** Called after the on-chain step(s) succeed so the screen can refetch. */
  onPaid?: () => void;
}

/**
 * The buyer's payment into a delivery escrow (contracts v3 §2.1, UI §2/§4; api#380 §2.9).
 *
 * On Base this is the buyer's own three prompts: their wallet **creates the escrow contract** from
 * the terms sheet the api signed, then approves exactly what the contract will pull, then
 * `fund()`. The platform sends no transaction and keeps no gas line — the network fees of the
 * three transactions are the wallet's own, a footnote under the total rather than a currency in
 * it. A deal the platform deployed (the path still live until the switch, and every deal from
 * before it) is the two prompts it always was. What remains of the old flow is the legacy TRON
 * tail, which still owes a TRX transfer and is kept working for the deals that were created under
 * it.
 *
 * The sequence itself — create, remember the hash, report it, approve, fund, and what to do when a
 * step fails or the page was reloaded between two of them — is `payEscrow` (`src/utils/
 * escrowCreation.ts`), pure and unit-tested; this dialog prepares the delivery pieces and renders.
 *
 * The amount and the token come from the deployed clone, never from this screen's arithmetic: the
 * buyer approves the number `fund()` will actually take, and an under-approval is caught by the
 * wallet layer with a sentence that says so rather than by a revert with nothing to read.
 */
export const DeliveryPayModal: FC<DeliveryPayModalProps> = ({
  visible,
  onClose,
  deal,
  chain,
  mode,
  onPaid,
}) => {
  const walletUI = useWalletUI(chain?.family);
  const { reportGasPaid } = useDeliveryGasPaid();
  const { reportEscrowCreated } = useDeliveryEscrowCreated();
  const fetchDeal = useFetchDeliveryDeal();
  const tx = useTxProgress();

  useClearWhenWalletConnects(tx, chain?.family);

  const [error, setError] = useState('');
  const [gasWarning, setGasWarning] = useState('');
  // A line about something unusual the payment is handling (a creation sent before this screen opened).
  const [notice, setNotice] = useState('');
  const [success, setSuccess] = useState(false);
  // Set before anything async runs. `tx.isRunning` only turns true once the wallet is reached, and
  // the contract read before it makes three RPC calls and may sleep 3s on a read-after-deploy miss
  // — several seconds in which the button would still accept a second press and start a second
  // approve and fund. The second `fund()` reverts, but the buyer pays for it.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A creation transaction this browser sent for the deal and never saw reported (api#380).
  const stashedCreation = useEscrowStash(deal.id, deal.contractAddress);
  // What the dialog promised when the run started, kept until it ends: the api's answer to the
  // report arrives in the deal prop mid-run, and the three prompts must not turn into two under
  // a panel that says "Step 2 of 3".
  const [runThreePrompts, setRunThreePrompts] = useState<boolean | null>(null);

  const lines = deliveryInvoiceLines(deal);
  const invoice = deliveryInvoiceView(deal, chain);
  const gasSun = deliveryGasSun(lines.gasTrx);
  const owesGas = hasGasCompensation(lines) && deal.gasStatus === DeliveryGasStatus.Pending;
  const isEvm = chain?.family === 'evm';
  // The buyer's wallet creates the contract (api#380): a signed sheet and no contract yet.
  const needsEscrow = mode === 'fund' && !deal.contractAddress && !!deal.escrowTerms;
  // Three prompts when the contract is still to be created; a stashed creation counts as its first.
  const threePrompts = runThreePrompts ?? (needsEscrow || (!deal.contractAddress && !!stashedCreation));

  const isGasOnly = mode === 'gas';
  // The dialog locks while the wallet and the chain are working, and unlocks the moment the payment
  // is through — the last phase is the *server* catching up, which the deal screen behind this
  // dialog is already showing. Leaving it locked there would trap the buyer in a dialog whose own
  // button reads "Close".
  const isProcessing = (isSubmitting || tx.isRunning) && !success;

  const handleClose = () => {
    if (isProcessing) {
      return;
    }
    setError('');
    setGasWarning('');
    setNotice('');
    setSuccess(false);
    setRunThreePrompts(null);
    tx.reset();
    onClose();
  };

  /** The deal as the payment flow reads it: the address, the sheet, and whether it still takes a payment. */
  const viewOf = (detail: ApiDeliveryDealDetail) => ({
    contractAddress: detail.contractAddress,
    escrowTerms: detail.escrowTerms,
    payable: detail.status === DeliveryDealStatus.AwaitingPayment,
  });

  const handleConfirm = async () => {
    if (isProcessing) {
      return;
    }

    // A run of this deal is still at its prompt in the wallet, or waiting on the network — maybe
    // from a dialog that has since been closed: no second run until it settles.
    if (flowInFlight(deal.id)) {
      setError(FLOW_IN_FLIGHT_MESSAGE);

      return;
    }

    setError('');
    setGasWarning('');
    setNotice('');
    setRunThreePrompts(threePrompts);
    setIsSubmitting(true);

    const gasKey = gasTxStashKey(deal.id);

    try {
      // (1) The legacy TRON gas compensation. The transfer and the report are two independent
      // steps with the tx hash stashed between them: if the TRX leaves the wallet but the report
      // fails, the stash lets the next attempt re-report the same hash instead of paying again.
      // Only a deployed clone owes it — the platform address is read off the clone.
      if (owesGas) {
        if (!deal.contractAddress) {
          throw new Error('The escrow contract is not ready yet. Please try again shortly.');
        }

        const { platform } = await readDeliveryContractInfo(
          walletUI,
          deal.contractAddress,
          chain?.chainId
        );
        let gasHash = readStash(gasKey);

        if (!gasHash) {
          try {
            const gasResult = await withWalletTimeout(
              walletUI.sendTransaction({ to: platform, amount: gasSun, functionName: undefined })
            );
            gasHash = gasResult.hash;
            writeStash(gasKey, gasHash);
          } catch (gasErr) {
            // The transfer itself failed → nothing was sent. In gas-only mode this is the whole
            // point (surface it); in fund mode continue to escrow.
            if (isGasOnly) {
              throw gasErr;
            }
            const message = gasErr instanceof Error ? gasErr.message : 'Gas payment failed.';
            setGasWarning(`${message} You can settle the gas fee later from the deal screen.`);
          }
        }

        if (gasHash) {
          try {
            await reportGasPaid(deal.id, gasHash);
            clearStash(gasKey);
          } catch {
            // The TRX already left the wallet — KEEP the stash so the next attempt re-reports this
            // hash rather than paying again.
            if (isGasOnly) {
              throw new Error(
                `Gas was sent (tx ${gasHash}) but could not be reported. Reopen this screen to retry — do not pay it again.`
              );
            }
            setGasWarning(
              `Gas was sent (tx ${gasHash}) but could not be reported yet — it will retry automatically. Do not pay it again.`
            );
          }
        }
      } else {
        // Gas already confirmed paid elsewhere (or never owed) — drop any stale stash.
        clearStash(gasKey);
      }

      if (isGasOnly) {
        setSuccess(true);
        onPaid?.();

        return;
      }

      // (2) Create the contract if the buyer is to (one prompt, the hash remembered at once), then
      // (3) approve price+delivery+fee → (4) fund(). Both the amount and the settlement token come
      // from the contract, so neither depends on client config.
      const paid = await tx.run((onProgress, signal) => {
        const flow = payEscrow({
          dealId: deal.id,
          chainId: chain?.chainId,
          deal: viewOf(deal),
          createCall: deliveryCreateCall,
          walletUI,
          report: async (txHash) => viewOf(await reportEscrowCreated(deal.id, txHash)),
          refresh: async () => viewOf(await fetchDeal(deal.id)),
          readContract: (address) => readDeliveryContractInfo(walletUI, address, chain?.chainId),
          paySignature: 'fund()',
          onProgress,
          onNotice: setNotice,
          signal,
        });

        // Remembered, outside this dialog, until it settles — however the race in `tx.run` ends.
        rememberFlow(deal.id, flow);

        return flow;
      }, 'Transaction failed. Please try again.');

      if (paid) {
        setSuccess(true);
        onPaid?.();
      }
      // Otherwise the run stopped; whatever is stashed is what the next press continues from, and
      // the dialog reads it afresh (`useEscrowStash`).
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Transaction failed. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
      // The promise of this run ends with it; the next run reads the deal afresh.
      setRunThreePrompts(null);
    }
  };

  const header = isGasOnly ? 'Pay the gas fee' : 'Pay for this order';
  const buttonText = success
    ? 'Close'
    : isProcessing
      ? 'Processing…'
      : isGasOnly
        ? `Pay ${lines.gasTrx} TRX`
        : 'Pay now';
  // Counted, not assumed: a v3 deal the platform deployed is two prompts, one the buyer creates
  // is three, and a legacy TRON deal that still owes the gas compensation is three of its own.
  // Promising the wrong number is how a buyer decides something went wrong and closes the wallet
  // mid-payment.
  const promptsTitle = isGasOnly
    ? 'One wallet prompt'
    : threePrompts
      ? 'Create escrow, approve, pay'
      : owesGas
        ? 'Three wallet prompts will appear'
        : 'Two wallet prompts will appear';

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      icon={
        success ? (
          <CheckCircle size={64} color="white" weight="fill" />
        ) : (
          <Truck size={64} color="white" weight="fill" />
        )
      }
      header={success ? 'Payment sent' : header}
      buttonText={buttonText}
      onSubmit={success ? handleClose : handleConfirm}
      disabled={isProcessing}
    >
      <Stack gap={3} width="100%">
        {success ? (
          <Stack gap={1} className={styles.info}>
            <Typography color="white" fontWeight="600">
              {isGasOnly
                ? 'Gas fee sent. It will be marked as paid shortly.'
                : 'Payment confirmed. Your order status will update shortly.'}
            </Typography>
          </Stack>
        ) : (
          <>
            {isGasOnly ? (
              <Stack gap={1} className={styles.info}>
                <Row label="Gas compensation" value={`${lines.gasTrx} TRX`} />
              </Stack>
            ) : (
              <InvoiceCard
                title="You are paying"
                lines={invoice.lines}
                total={invoice.total}
                footnote={invoice.footnote}
                note={threePrompts && isEvm ? ESCROW_NETWORK_FEES_NOTE : null}
                issuedAt={invoice.issuedAt}
                chain={chain}
              />
            )}

            <Stack gap={1} className={styles.warning}>
              <Stack flexDirection="row" gap={1} alignItems="center">
                <Warning size={20} color="white" weight="fill" />
                <Typography color="white" fontWeight="600">
                  {promptsTitle}
                </Typography>
              </Stack>
              <Typography color="lightWhite" variant="small">
                {isGasOnly ? (
                  <>Send the gas compensation in TRX to the platform.</>
                ) : threePrompts ? (
                  <>
                    Three wallet prompts will appear: first your wallet creates the escrow contract
                    for this order (a small network fee), then an{' '}
                    <b>approve of exactly {invoice.total.value}</b>, then the payment. Your money is
                    held in escrow and released when the car is picked up; the platform fee is
                    non-refundable.
                    {isEvm ? ` ${ESCROW_ETH_BALANCE_HINT}` : ''}
                  </>
                ) : (
                  <>
                    {owesGas ? (
                      <>First a TRX transfer for the gas compensation, then an </>
                    ) : (
                      <>First an </>
                    )}
                    <b>approve of exactly {invoice.total.value}</b> (the escrow amount, not a
                    network fee — do not lower it), then the payment itself. Your money is held in
                    escrow and released when the car is picked up; the platform fee is
                    non-refundable.
                    {isEvm
                      ? ' You need a small ETH balance on this network to pay for the two transactions.'
                      : ''}
                  </>
                )}
              </Typography>
            </Stack>

            {stashedCreation && !isProcessing ? (
              <Typography color="white" variant="small" className={styles.warning}>
                An escrow creation was already sent for this order (tx {stashedCreation}). Pay now
                reports it first — the escrow is not created again.
              </Typography>
            ) : null}
          </>
        )}

        <TxProgress
          state={tx.state}
          done={success}
          error={tx.error}
          // No wallet to sign with: `Connect Wallet` and the connection window stand in for Retry (app#315).
          connect={tx.needsWallet && chain ? <WalletButton family={chain.family} /> : null}
          explorerTxUrl={chain?.explorerTxUrl}
          onRetry={() => {
            tx.reset();
            handleConfirm();
          }}
        />

        {notice && isProcessing ? (
          <Typography color="lightWhite" variant="small">
            {notice}
          </Typography>
        ) : null}

        {gasWarning ? (
          <Typography color="white" variant="small" className={styles.warning}>
            {gasWarning}
          </Typography>
        ) : null}

        {error ? (
          <Typography color="white" variant="small" className={styles.error}>
            {error}
          </Typography>
        ) : null}
      </Stack>
    </Modal>
  );
};

const Row: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack flexDirection="row" justifyContent="space-between" gap={2}>
    <Typography color="lightWhite" variant="small">
      {label}
    </Typography>
    <Typography color="white" variant="small">
      {value}
    </Typography>
  </Stack>
);
